// VOIP Dashboard Constants and Utility Functions

import { VoIPDevice, VoIPStats, VoIPAlert, CallRecord, StationStats } from './voipTypes';

// Calculate statistics from device list (ICMP Ping based)
export const calculateStats = (devices: VoIPDevice[]): VoIPStats => {
  const totalDevices = devices.length;
  const onlineDevices = devices.filter(d => d.status === 'online').length;
  const offlineDevices = devices.filter(d => d.status === 'offline').length;
  const registeredDevices = devices.filter(d => d.registrationStatus === 'registered').length;
  const activeCalls = devices.filter(d => d.callStatus === 'active' || d.callStatus === 'ringing').length;

  // Calculate average latency/RTT from online devices
  const onlineWithLatency = devices.filter(d => d.status === 'online' && d.latency > 0);
  const avgLatency = onlineWithLatency.length > 0
    ? Math.round(onlineWithLatency.reduce((sum, d) => sum + d.latency, 0) / onlineWithLatency.length)
    : 0;

  // Calculate average jitter
  const onlineWithJitter = devices.filter(d => d.status === 'online' && d.jitter > 0);
  const avgJitter = onlineWithJitter.length > 0
    ? Math.round(onlineWithJitter.reduce((sum, d) => sum + d.jitter, 0) / onlineWithJitter.length * 10) / 10
    : 0;

  // Calculate average packet loss
  const devicesWithPacketLoss = devices.filter(d => d.packetLoss >= 0);
  const avgPacketLoss = devicesWithPacketLoss.length > 0
    ? Math.round(devicesWithPacketLoss.reduce((sum, d) => sum + d.packetLoss, 0) / devicesWithPacketLoss.length * 100) / 100
    : 0;

  // Count alerts: offline devices + high latency + high packet loss
  const alertCount = devices.filter(d =>
    d.status === 'offline' ||
    (d.status === 'online' && d.latency > 100) ||
    (d.status === 'online' && d.packetLoss > 1)
  ).length;

  return {
    totalDevices,
    onlineDevices,
    offlineDevices,
    activeAlerts: alertCount,
    activeCalls,
    avgLatency,
    avgJitter,
    avgPacketLoss,
    registeredDevices,
  };
};

// Calculate station-wise statistics
export const calculateStationStats = (devices: VoIPDevice[]): StationStats[] => {
  const stationMap = new Map<string, StationStats>();

  devices.forEach(device => {
    const existing = stationMap.get(device.station);
    if (existing) {
      existing.totalDevices++;
      if (device.status === 'online') existing.online++;
      if (device.status === 'offline') existing.offline++;
      if (device.callStatus === 'active') existing.activeCalls++;
    } else {
      stationMap.set(device.station, {
        station: device.station,
        totalDevices: 1,
        online: device.status === 'online' ? 1 : 0,
        offline: device.status === 'offline' ? 1 : 0,
        activeCalls: device.callStatus === 'active' ? 1 : 0,
      });
    }
  });

  return Array.from(stationMap.values());
};

// Get quality label based on metrics
export const getQualityLabel = (latency: number, jitter: number, packetLoss: number): string => {
  if (latency <= 50 && jitter <= 10 && packetLoss <= 0.5) return 'Excellent';
  if (latency <= 100 && jitter <= 20 && packetLoss <= 1) return 'Good';
  if (latency <= 200 && jitter <= 30 && packetLoss <= 2) return 'Fair';
  return 'Poor';
};

// Get quality color based on metrics
export const getQualityColor = (latency: number, jitter: number, packetLoss: number): string => {
  const quality = getQualityLabel(latency, jitter, packetLoss);
  switch (quality) {
    case 'Excellent': return '#28a745';
    case 'Good': return '#17a2b8';
    case 'Fair': return '#ffc107';
    default: return '#dc3545';
  }
};

// Status color mapping
export const statusColors = {
  online: '#28a745',
  offline: '#dc3545',
  warning: '#ffc107',
  registered: '#28a745',
  unregistered: '#6c757d',
  failed: '#dc3545',
  idle: '#6c757d',
  ringing: '#17a2b8',
  active: '#28a745',
  hold: '#ffc107',
};

// Format duration in seconds to readable string
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

// Format uptime to readable string
export const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

// Default codec options
export const codecOptions = ['G.711', 'G.722', 'G.729', 'Opus'];

// Generate mock data for development
export const generateMockDevices = (count: number = 20): VoIPDevice[] => {
  const stations = ['Station A', 'Station B', 'Station C', 'Station D', 'Station E'];
  const models = ['Cisco IP Phone 8845', 'Polycom VVX 450', 'Yealink T54W', 'Grandstream GXP2170'];
  const codecs = ['G.711', 'G.722', 'G.729', 'Opus'];
  const statuses: ('online' | 'offline' | 'warning')[] = ['online', 'online', 'online', 'online', 'offline', 'warning'];
  const regStatuses: ('registered' | 'unregistered' | 'failed')[] = ['registered', 'registered', 'registered', 'unregistered', 'failed'];
  const callStatuses: ('idle' | 'ringing' | 'active' | 'hold')[] = ['idle', 'idle', 'idle', 'active', 'ringing', 'hold'];

  return Array.from({ length: count }, (_, i) => {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const isOnline = status === 'online';

    return {
      id: `voip-${i + 1}`,
      deviceId: `DEV-${String(i + 1).padStart(4, '0')}`,
      name: `VoIP Phone ${i + 1}`,
      station: stations[Math.floor(Math.random() * stations.length)],
      ipAddress: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      macAddress: Array.from({ length: 6 }, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join(':').toUpperCase(),
      status,
      registrationStatus: isOnline ? regStatuses[Math.floor(Math.random() * 3)] : regStatuses[Math.floor(Math.random() * regStatuses.length)],
      callStatus: isOnline ? callStatuses[Math.floor(Math.random() * callStatuses.length)] : 'idle',
      lastActivity: new Date(Date.now() - Math.random() * 3600000).toISOString(),
      signalStrength: isOnline ? 70 + Math.floor(Math.random() * 30) : 0,
      latency: isOnline ? 20 + Math.floor(Math.random() * 80) : 0,
      jitter: isOnline ? Math.floor(Math.random() * 20 * 10) / 10 : 0,
      packetLoss: isOnline ? Math.floor(Math.random() * 30) / 10 : 0,
      codec: codecs[Math.floor(Math.random() * codecs.length)],
      extension: `${1000 + i}`,
      firmware: `v${Math.floor(Math.random() * 5) + 1}.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 100)}`,
      model: models[Math.floor(Math.random() * models.length)],
      uptime: isOnline ? Math.floor(Math.random() * 86400 * 30) : 0,
      totalCalls: Math.floor(Math.random() * 500),
      missedCalls: Math.floor(Math.random() * 50),
      assetDetails: {
        make: ['Cisco', 'Polycom', 'Yealink', 'Grandstream'][Math.floor(Math.random() * 4)],
        model: models[Math.floor(Math.random() * models.length)],
        serialNumber: `SN${Math.floor(Math.random() * 1000000)}`,
        installationDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000 * 2).toISOString().split('T')[0],
        warrantyExpiry: new Date(Date.now() + Math.random() * 365 * 24 * 60 * 60 * 1000 * 2).toISOString().split('T')[0],
        location: `Room ${Math.floor(Math.random() * 100) + 1}`,
        zone: `Zone ${String.fromCharCode(65 + Math.floor(Math.random() * 5))}`,
        division: `Division ${Math.floor(Math.random() * 3) + 1}`,
      },
    };
  });
};

// Generate mock alerts
export const generateMockAlerts = (devices: VoIPDevice[]): VoIPAlert[] => {
  const alerts: VoIPAlert[] = [];

  devices.forEach(device => {
    if (device.status === 'offline') {
      alerts.push({
        id: `alert-${device.id}-offline`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: 'critical',
        message: `Device is offline - Last seen: ${new Date(device.lastActivity).toLocaleString()}`,
        timestamp: new Date().toISOString(),
      });
    }
    if (device.registrationStatus === 'failed') {
      alerts.push({
        id: `alert-${device.id}-reg`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: 'critical',
        message: 'SIP registration failed - Check network connectivity',
        timestamp: new Date().toISOString(),
      });
    }
    if (device.latency > 150) {
      alerts.push({
        id: `alert-${device.id}-latency`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: 'warning',
        message: `High latency detected: ${device.latency}ms`,
        timestamp: new Date().toISOString(),
      });
    }
    if (device.packetLoss > 2) {
      alerts.push({
        id: `alert-${device.id}-packet`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: 'warning',
        message: `High packet loss: ${device.packetLoss}%`,
        timestamp: new Date().toISOString(),
      });
    }
  });

  return alerts.sort((a, b) => (a.type === 'critical' ? -1 : 1));
};

// Generate mock call history
export const generateMockCallHistory = (devices: VoIPDevice[], count: number = 50): CallRecord[] => {
  const callTypes: ('incoming' | 'outgoing' | 'missed')[] = ['incoming', 'outgoing', 'missed'];
  const qualities: ('excellent' | 'good' | 'fair' | 'poor')[] = ['excellent', 'good', 'good', 'fair', 'poor'];

  return Array.from({ length: count }, (_, i) => {
    const device = devices[Math.floor(Math.random() * devices.length)];
    const callType = callTypes[Math.floor(Math.random() * callTypes.length)];
    const duration = callType === 'missed' ? 0 : Math.floor(Math.random() * 1800);
    const startTime = new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000);
    const endTime = new Date(startTime.getTime() + duration * 1000);

    return {
      id: `call-${i + 1}`,
      deviceId: device.id,
      deviceName: device.name,
      extension: device.extension || '',
      station: device.station,
      callType,
      callerNumber: `+91${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      duration,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      quality: callType === 'missed' ? 'poor' : qualities[Math.floor(Math.random() * qualities.length)],
    };
  }).sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
};
